// 表單區塊 — 把一堆欄位分成「這一區在問什麼」。
//
// 2026-07-19 (使用者要求「輸入資料的頁面設計也可以用區塊, 比較知道哪邊要輸入什麼」)。
//
// ⚠ DESIGN_GUIDE §4.4 早就把這個元件的規格寫好了 (小標 `text-sm font-semibold
// text-slate-700`、內容 `space-y-3`、section 間距 `space-y-5`) —— 只是**從來沒有人
// 把它建出來**, 所以五張簽核表單各自把欄位平鋪成一長串, 沒有任何分組。
//
// 小標用 text-sm 不是 text-xs: 指南 §2 明講「Section 小標 = heading, 不可比內文小」
// —— 它是一段內容的開頭, 不是標示下面那團東西的 label。
import type { ReactNode } from 'react';

export function FormSection({
  title,
  hint,
  children,
  /** 右上角動作 (例如報帳明細的「+ 加一列」) */
  action,
  /**
   * 去掉外框 —— 給**唯讀**檢視用 (簽核詳情頁)。
   *
   * 2026-08-16 使用者:「這一頁的頁面設計 有沒有改善的空間」。 填表時每區一張卡是
   * 對的 (要清楚知道哪一格填什麼); 但同一份文件切成唯讀之後, 五張帶標題列與邊框的
   * 卡片裝十來個唯讀值 = 一大半的高度都是外框, 核准鈕被推到捲軸外面。 flat 讓呼叫端
   * 用**一張**卡包住全部區塊, 區塊之間只留一條細線。
   *
   * 2026-09-02 使用者:「如果已經有區塊的分別就不用再有大標題 (分隔線可以明顯一點)」
   * —— title 改選填: 泛稱容器標題 (基本資料/金額/其他資訊) 在唯讀模式不傳,
   * 欄位標籤自己會講話; 分隔線 slate-100 → slate-200 補明顯度。
   * 同日補:「編輯內容頁跟新增頁 請follow 上面說的頁面格式」—— 六份 *Document 的
   * edit (drawer) 模式也改走 flat, 上面「填表時每區一張卡」的理由被使用者推翻;
   * 帶邊框的卡片版仍給非簽核表單 (記帳分錄 / 薪酬 …) 用。
   */
  flat,
}: {
  title?: string;
  hint?: string;
  children: ReactNode;
  action?: ReactNode;
  flat?: boolean;
}) {
  if (flat) {
    return (
      <section className="pt-3 first:pt-0 border-t first:border-t-0 border-slate-200">
        {(title || hint || action) && (
          <div className="flex items-baseline gap-2 mb-2">
            {title && <h3 className="text-sm font-semibold text-slate-700">{title}</h3>}
            {hint && <span className="text-xs text-slate-400">{hint}</span>}
            {action && <div className="ml-auto">{action}</div>}
          </div>
        )}
        <div className="space-y-3">{children}</div>
      </section>
    );
  }
  return (
    <section className="border border-slate-200 rounded-lg">
      {(title || hint || action) && (
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-baseline gap-2">
          {title && <h3 className="text-sm font-semibold text-slate-700">{title}</h3>}
          {hint && <span className="text-xs text-slate-400">{hint}</span>}
          {action && <div className="ml-auto">{action}</div>}
        </div>
      )}
      <div className="p-4 space-y-3">{children}</div>
    </section>
  );
}

/** 表單 label — DESIGN_GUIDE §4.2。 五張簽核表單原本各自用 text-xs/slate-600。 */
export const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

/** 欄位下方的補充說明 — 指南 §4.2 hint。 */
export const hintClass = 'text-xs text-slate-500 mt-1';

/**
 * 送出列 — 指南 §4.7 (右對齊、次要在左、`pt-2 border-t border-slate-200`)。
 *
 * ⚠ 2026-08-27 使用者：「按鈕邏輯 都是 往前的是綠色(實心)」——**簽核類**抽屜的
 * 「儲存並送出 / 送出簽核」會把單子推進流程 → 已改用
 * `decision-button.ts` 的 `decisionCls('go')`（綠實心）+ `quiet`（草稿 / 取消）。
 * `footerRowClass` 這個容器仍然共用。 底下兩個 btn*Class 留給**非簽核**的表單
 * （記帳分錄 / 薪酬 / 股權 / 寄信 …），那些的「儲存」不推進任何流程。
 */
export const footerRowClass = 'flex justify-end gap-2 pt-2 border-t border-slate-200';
/**
 * 抽屜底部**有終結性動作**時的容器（刪除草稿靠左、儲存 / 送出靠右）。
 *
 * 逐字沿用簽核詳情頁 2026-09-02 定的版面：「終結性動作靠左隔開，其餘靠右、
 * 主要動作(綠)最右」。 不要改成在 `footerRowClass` 後面串 `justify-between`
 * —— Tailwind 的勝負看 CSS 檔裡的順序、不是 class 字串的順序。
 */
export const footerSplitRowClass =
  'flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200';
export const btnSecondaryClass =
  'px-4 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50';
export const btnPrimaryClass =
  'px-4 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800 disabled:opacity-50';
